/**
 * Team Authorization Helpers
 *
 * Canonical permission helpers for UI authorization decisions.
 * Governed by: CANONICAL_RBAC_CONTRACT_V1 v1.2
 *
 * RULES:
 * - All UI permission checks must call these helpers.
 * - No UI component may contain inline role === 'owner' comparisons.
 * - jobRole must never appear in any permission check.
 * - These helpers derive from teamRole only.
 * - All helpers read from ROLE_CAPABILITIES table (single source of truth).
 */

export type TeamRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';

/**
 * Canonical capability definition per RBAC V1.2 contract §5.1
 */
export type RoleCapabilities = {
  // Member management
  canInviteMembers: boolean;
  canRevokeInvites: boolean;
  canDeactivateMembers: boolean;
  canChangeRoles: boolean; // Change non-owner roles
  canPromoteToOwner: boolean; // Owner-only

  // Billing
  canViewBilling: boolean;
  canManageBilling: boolean; // Owner-only

  // Operational entities
  canWriteEntities: boolean; // Create/update
  canDeleteEntities: boolean;
  canExportEntities: boolean;

  // Finance visibility
  canViewFinancials: boolean; // Cost/margin/profit
};

/**
 * Canonical Role → Capabilities Matrix
 *
 * This is the SINGLE SOURCE OF TRUTH for all role-based permissions.
 * Matches CANONICAL_RBAC_CONTRACT_V1 v1.2 §5.1 exactly.
 *
 * DO NOT modify this table without updating the contract.
 */
const ROLE_CAPABILITIES: Record<TeamRole, RoleCapabilities> = {
  owner: {
    canInviteMembers: true,
    canRevokeInvites: true,
    canDeactivateMembers: true,
    canChangeRoles: true,
    canPromoteToOwner: true,
    canViewBilling: true,
    canManageBilling: true,
    canWriteEntities: true,
    canDeleteEntities: true,
    canExportEntities: true,
    canViewFinancials: true,
  },
  admin: {
    canInviteMembers: true,
    canRevokeInvites: true,
    canDeactivateMembers: true,
    canChangeRoles: true,
    canPromoteToOwner: false, // Owner-only
    canViewBilling: true,
    canManageBilling: false, // Owner-only
    canWriteEntities: true,
    canDeleteEntities: true,
    canExportEntities: true,
    canViewFinancials: true,
  },
  manager: {
    canInviteMembers: false,
    canRevokeInvites: false,
    canDeactivateMembers: false,
    canChangeRoles: false,
    canPromoteToOwner: false,
    canViewBilling: false,
    canManageBilling: false,
    canWriteEntities: true,
    canDeleteEntities: false,
    canExportEntities: false,
    canViewFinancials: true, // Manager can view financials
  },
  member: {
    canInviteMembers: false,
    canRevokeInvites: false,
    canDeactivateMembers: false,
    canChangeRoles: false,
    canPromoteToOwner: false,
    canViewBilling: false,
    canManageBilling: false,
    canWriteEntities: true,
    canDeleteEntities: false,
    canExportEntities: false,
    canViewFinancials: false, // Member cannot view financials
  },
  viewer: {
    canInviteMembers: false,
    canRevokeInvites: false,
    canDeactivateMembers: false,
    canChangeRoles: false,
    canPromoteToOwner: false,
    canViewBilling: false,
    canManageBilling: false,
    canWriteEntities: false, // Viewer is read-only
    canDeleteEntities: false,
    canExportEntities: false,
    canViewFinancials: false,
  },
};

/**
 * Empty capabilities for null/undefined roles.
 * All permissions denied.
 */
const EMPTY_CAPABILITIES: RoleCapabilities = {
  canInviteMembers: false,
  canRevokeInvites: false,
  canDeactivateMembers: false,
  canChangeRoles: false,
  canPromoteToOwner: false,
  canViewBilling: false,
  canManageBilling: false,
  canWriteEntities: false,
  canDeleteEntities: false,
  canExportEntities: false,
  canViewFinancials: false,
};

/**
 * Get capabilities for a role.
 * Returns empty capabilities for null/undefined roles.
 *
 * @param role - Team role or null/undefined
 * @returns Role capabilities object
 */
export function capabilitiesFor(role?: TeamRole | null): RoleCapabilities {
  if (!role || !ROLE_CAPABILITIES[role]) {
    return EMPTY_CAPABILITIES;
  }
  return ROLE_CAPABILITIES[role];
}

// ============================================================================
// Permission Helpers (Thin Wrappers Around Capability Table)
// ============================================================================

/**
 * Returns true if the role is 'owner'.
 * Contract §3: Owner has full control.
 */
export function isOwner(role?: TeamRole | null): boolean {
  return role === 'owner';
}

/**
 * Returns true if the actor is an admin-level role (owner or admin).
 * Contract §3: Owner and admin only. Manager is NOT an admin role.
 */
export function isAdminRole(role?: TeamRole | null): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Returns true if the actor can invite team members.
 * Contract §5.1: Owner + Admin
 */
export function canInviteMembers(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canInviteMembers;
}

/**
 * Returns true if the actor can revoke team invites.
 * Contract §5.1: Owner + Admin
 */
export function canRevokeInvites(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canRevokeInvites;
}

/**
 * Returns true if the actor can deactivate/reactivate members.
 * Contract §5.1: Owner + Admin
 */
export function canDeactivateMembers(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canDeactivateMembers;
}

/**
 * Returns true if the actor can change member roles (non-owner roles).
 * Contract §5.1: Owner + Admin
 * Note: Promoting to owner requires canPromoteToOwner (owner-only).
 */
export function canChangeRoles(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canChangeRoles;
}

/**
 * Returns true if the actor can promote members to owner.
 * Contract §5.1: Owner only
 */
export function canPromoteToOwner(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canPromoteToOwner;
}

/**
 * Returns true if the actor can view billing/subscription information.
 * Contract §5.1: Owner + Admin
 */
export function canViewBilling(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canViewBilling;
}

/**
 * Returns true if the actor can manage billing (upgrade, cancel, payment methods).
 * Contract §5.1: Owner only
 */
export function canManageBilling(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canManageBilling;
}

/**
 * Returns true if the actor can create or update entities.
 * Contract §5.1: All except Viewer
 */
export function canWriteEntities(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canWriteEntities;
}

/**
 * Returns true if the actor can delete entities.
 * Contract §5.1: Owner + Admin
 */
export function canDeleteEntities(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canDeleteEntities;
}

/**
 * Returns true if the actor can export data.
 * Contract §5.1: Owner + Admin
 */
export function canExportEntities(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canExportEntities;
}

/**
 * Returns true if the actor can view financial data (cost, margin, profit).
 * Contract §5.1: Owner + Admin + Manager
 */
export function canViewFinancials(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canViewFinancials;
}

/**
 * Returns true if the actor can cancel the team subscription.
 * Contract §5.1: Owner only
 */
export function canCancelSubscription(role?: TeamRole | null): boolean {
  return capabilitiesFor(role).canManageBilling;
}

/**
 * @deprecated Use canInviteMembers() + canDeactivateMembers() instead.
 * This helper combined multiple capabilities and is being phased out.
 */
export function canManageMembers(role?: TeamRole | null): boolean {
  return canInviteMembers(role);
}

/**
 * @deprecated Moved to types/team.ts, will be removed.
 * Use canWriteEntities() or specific capability checks instead.
 */
export function canDisableMembers(role?: TeamRole | null): boolean {
  return canDeactivateMembers(role);
}
